import React, { useState } from 'react';
import { ActualButton, Card, Page, Switcher, InputGroup, Input } from '@nearst/ui';
import { Protect } from '@clerk/clerk-react';

import ImageInput from '@components/ImageInput';
import AuthorizationTooltip from '@components/AuthorizationTooltip';
import AccessDenied from '../../../errors/AccessDenied';
import newLocationContact from '@utils/newLocationContact';
import { useShop } from '@services/ShopProvider';
import { useCheckPermission } from '@services/auth/hooks';

import styles from './LocationProfile.module.scss';
import ProfileDefaultImage from '../../../../assets/default-profile.png';

const validators = {
	contactName: (value) => ({
		isValid: value?.trim().length > 0,
		error: 'Contact name is required'
	}),

	contactEmail: (value) => ({
		isValid: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
		error: 'Please enter a valid email address'
	}),

	name: (value) => ({
		isValid: value?.trim().length > 0,
		error: 'Shop name is required'
	}),

	publicPhoneNumber: (value) => ({
		isValid: /^\+?[\d\s-()]+$/.test(value?.trim()),
		error: 'Please enter a valid phone number'
	})
};

const validateField = (field, value) => {
	const validator = validators[field];
	if (!validator) return { isValid: true };

	const result = validator(value);
	return {
		isValid: result.isValid,
		error: result.isValid ? null : result.error
	};
};

const ValidatableInput = ({ authorized, inputProps, errorMessage = '' }) => (
	<div>
		<AuthorizationTooltip authorized={authorized}>
			<Input {...inputProps} />
		</AuthorizationTooltip>
		{errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
	</div>
);

const LocationProfile = () => {
	const { shop, updateShop } = useShop();
	const authorized = useCheckPermission('org:locations:manage');
	const [errors, setErrors] = useState({});

	const handleBlur = ({ target }) => {
		const { id, value } = target;
		const validation = validateField(id, value.trim());

		setErrors((prev) => ({
			...prev,
			[id]: validation.error
		}));

		if (!authorized || !validation.isValid) {
			return;
		}

		if (value.trim() !== shop[id]) {
			updateShop(shop.id, { [id]: value.trim() });
		}
	};

	const getInputProps = (id) => ({
		id,
		defaultValue: shop[id],
		onBlur: handleBlur,
		disabled: !authorized,
		error: errors[id],
		required: true
	});

	return (
		<Protect permission="org:locations:read" fallback={<AccessDenied />}>
			<Page.Header>
				<h1>Location profile</h1>
				<p>Edit the basic details of the location, used across all channels.</p>
			</Page.Header>
			<Page.Section>
				<Card
					title="Contact details"
					description="Details of the main contact of the account to contact about payments and legals."
				>
					<InputGroup
						label="Main contact name"
						htmlFor="contactName"
						className={errors.contactName && styles.formError}
						requiredField
					>
						<ValidatableInput
							authorized={authorized}
							inputProps={getInputProps('contactName')}
							errorMessage={errors.contactName}
						/>
					</InputGroup>
					<InputGroup
						label="Contact email"
						htmlFor="contactEmail"
						className={errors.contactEmail && styles.formError}
						requiredField
					>
						<ValidatableInput
							authorized={authorized}
							inputProps={getInputProps('contactEmail')}
							errorMessage={errors.contactEmail}
						/>
					</InputGroup>
				</Card>

				<Card title="Location details" description="Public information about your shop.">
					<InputGroup label="Shop name" htmlFor="name" className={errors.name && styles.formError} requiredField>
						<ValidatableInput authorized={authorized} inputProps={getInputProps('name')} errorMessage={errors.name} />
					</InputGroup>
					<InputGroup
						label="Public phone number"
						htmlFor="publicPhoneNumber"
						className={errors.publicPhoneNumber && styles.formError}
						requiredField
					>
						<ValidatableInput
							authorized={authorized}
							inputProps={getInputProps('publicPhoneNumber')}
							errorMessage={errors.publicPhoneNumber}
						/>
					</InputGroup>

					<div className={styles.helpText}>
						Want to change your shop address?{' '}
						<a href={`mailto:support@near.st?subject=Changing%20my%20shop%20address%20(${shop.legacyId})`}>Send us a message</a>
					</div>
				</Card>
				<Protect permission="org:locations:manage">
					<Switcher className={styles.imageUploaders}>
						<Card title="Favicon" description="Configure the icon shown for the shop page URL and your local listings.">
							<ImageInput
								displayName="Header image"
								field="iconImageUrl"
								placeholder="https://near.st/favicon.svg"
								containerId="header-image"
								imageClass={styles.profileFaviconPicture}
								authorized={authorized}
							/>
						</Card>
						<Card
							title="Location profile image"
							description="Help shoppers identify your location across all enabled channels."
						>
							<ImageInput
								displayName="Profile image"
								field="profileImageUrl"
								placeholder={ProfileDefaultImage}
								containerId="profile-image"
								imageClass={styles.profileShopPicture}
								authorized={authorized}
							/>
						</Card>
					</Switcher>

					<Card
						title="Use NearSt across your locations"
						description="Have another location? Sign it up for NearSt, and it will be added directly to your account."
					>
						<ActualButton onClick={newLocationContact}>Sign up a new location</ActualButton>
					</Card>
				</Protect>
			</Page.Section>
		</Protect>
	);
};

export default LocationProfile;
